import { useRef, useState, useEffect } from 'react';
import { useCategoryDocumentsContext } from '../../context/CategoryDocumentsContext';
import { useUpsertCategoryDocument } from '../../api/useUpsertCategoryDocument';
import { useDeleteCategoryDocument } from '../../api/useDeleteCategoryDocument';
import { useUploadFile } from '../../api/useUploadFile';
import { useDownloadFile } from '../../api/useDownloadFile';
import { useDeleteFile } from '../../api/useDeleteFile';
import {
    CategoryDocumentsContainer,
    DocumentList,
    DocumentItem,
    DeleteButton,
    FileLink,
    UploadSection,
    HiddenFileInput,
    UploadButton,
    StatusMessage,
    EmptyMessage,
    FileSelectionContainer,
    ConfirmUploadButton,
} from './CategoryDocuments.styles';
import { useEditMode } from '../../context/EditModeContext';
import LoadingSpinner from './LoadingSpinner';

interface CategoryDocumentsProps {
    category: string;
    title?: string;
    hideEmptyMessage?: boolean;
}

export const CategoryDocuments: React.FC<CategoryDocumentsProps> = ({ category, title, hideEmptyMessage = false }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const { getDocumentsByCategory, isLoading, error, refetch } = useCategoryDocumentsContext();
    const { upsertCategoryDocument, isUpserting, upsertError, upsertSuccess, clearMessages: clearUpsertMessages } = useUpsertCategoryDocument();
    const {
        deleteCategoryDocument,
        isDeleting: isDeletingRecord,
        deleteError,
        deleteSuccess,
        clearMessages: clearDeleteMessages,
    } = useDeleteCategoryDocument();
    const { deleteFile, isDeleting: isDeletingFile } = useDeleteFile();
    const { uploadFile, isUploading, uploadError, uploadSuccess, clearMessages: clearUploadMessages } = useUploadFile();
    const { downloadFile, isDownloading, downloadError } = useDownloadFile();
    const { isInEditMode } = useEditMode();

    const isDeleting = isDeletingRecord || isDeletingFile;

    // Get filtered documents for this category
    const categoryDocuments = getDocumentsByCategory(category);

    // Handle file selection
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
        }
    };

    // Handle file upload
    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            // Upload file to blob storage
            await uploadFile({
                category: category.toLowerCase(),
                fileName: selectedFile.name,
                file: selectedFile,
            });

            // Create database record
            await upsertCategoryDocument({
                filePath: selectedFile.name,
                category: category.toUpperCase(),
            });

            // Refresh document list
            await refetch();

            // Clear file input
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    // Handle file download
    const handleDownload = async (fileName: string) => {
        try {
            await downloadFile({
                category: category.toLowerCase(),
                fileName,
            });
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    // Handle document deletion (both blob file and database record)
    const handleDelete = async (documentId: number, fileName: string) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            // Delete file from blob storage
            await deleteFile({
                category: category.toLowerCase(),
                fileName,
            });

            // Delete database record
            await deleteCategoryDocument(documentId);

            // Refresh document list
            await refetch();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    // Clear success messages after delay
    useEffect(() => {
        if (uploadSuccess || upsertSuccess) {
            const timer = setTimeout(() => {
                clearUploadMessages();
                clearUpsertMessages();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [uploadSuccess, upsertSuccess]);

    useEffect(() => {
        if (deleteSuccess) {
            const timer = setTimeout(() => {
                clearDeleteMessages();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [deleteSuccess]);

    if (isLoading) {
        return <LoadingSpinner text="Loading documents..." />;
    }

    if (error) {
        return <StatusMessage type="error">Error loading documents: {error}</StatusMessage>;
    }

    return (
        <CategoryDocumentsContainer>
            {title && <h4>{title}</h4>}

            {/* Document List */}
            <DocumentList>
                {categoryDocuments.length === 0 ? (
                    !hideEmptyMessage && <EmptyMessage>No documents uploaded yet.</EmptyMessage>
                ) : (
                    categoryDocuments.map((doc) => (
                        <DocumentItem key={doc.id}>
                            {isInEditMode && (
                                <DeleteButton
                                    onClick={() => handleDelete(doc.id, doc.filePath)}
                                    disabled={isDeleting}
                                    title="Remove file"
                                    aria-label="Remove file"
                                >
                                    🗑️
                                </DeleteButton>
                            )}
                            <FileLink onClick={() => handleDownload(doc.filePath)} role="button" tabIndex={0}>
                                {doc.filePath}
                            </FileLink>
                        </DocumentItem>
                    ))
                )}
            </DocumentList>

            {/* Upload Section - Only in Edit Mode */}
            {isInEditMode && (
                <UploadSection>
                    <HiddenFileInput ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} aria-label="File upload input" />
                    <UploadButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || isUpserting}
                        aria-label={`Upload a new ${category} document`}
                    >
                        {isUploading || isUpserting ? (
                            <>⏳ Uploading...</>
                        ) : (
                            <>Upload a new {category} document...</>
                        )}
                    </UploadButton>

                    {selectedFile && !isUploading && (
                        <FileSelectionContainer>
                            <span>Selected: {selectedFile.name}</span>
                            <ConfirmUploadButton onClick={handleUpload} disabled={isUpserting}>
                                Confirm Upload
                            </ConfirmUploadButton>
                        </FileSelectionContainer>
                    )}
                </UploadSection>
            )}

            {/* Status Messages */}
            {uploadSuccess && upsertSuccess && <StatusMessage type="success">File uploaded successfully!</StatusMessage>}
            {(uploadError || upsertError) && <StatusMessage type="error">Error: {uploadError || upsertError}</StatusMessage>}
            {deleteSuccess && <StatusMessage type="success">Document deleted successfully!</StatusMessage>}
            {deleteError && <StatusMessage type="error">Error deleting document: {deleteError}</StatusMessage>}
            {downloadError && <StatusMessage type="error">Error downloading file: {downloadError}</StatusMessage>}
            {isDownloading && <LoadingSpinner text="Downloading..." size="small" />}
        </CategoryDocumentsContainer>
    );
};
